import styles from './ChatGroupAside.module.scss';
import { useBool } from '@app/base';
import { Icon } from '@app/ui';
import { CustomChat } from '@app/main/api';
import { chatifyApi, ChatListItemBox, CreateChatButton, useChatGroupContext } from '@webinex/chatify';
import { Input, Menu, MenuProps } from 'antd';
import { isEmpty } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

function useOnSelect() {
  const { openChat } = useChatGroupContext();

  return useCallback(
    (info: Parameters<NonNullable<MenuProps['onSelect']>>[0]) => openChat(info.key as string),
    [openChat],
  );
}

function useMenuItems(searchValue: string | null) {
  const { data } = chatifyApi.useGetChatListQuery();
  const chats = data as CustomChat[];

  return useMemo<MenuProps['items']>(() => {
    const filteredChats = searchValue
      ? chats?.filter(
          (c) =>
            c.name.toLowerCase().includes(searchValue.toLowerCase()) ||
            c.members.some((member) => member.name.toLowerCase().includes(searchValue.toLowerCase())),
        )
      : chats;

    return filteredChats
      ?.slice()
      .sort((a, b) => b.message.sentAt.localeCompare(a.message.sentAt))
      .map((x) => ({
        key: x.id,
        label: <ChatListItemBox chat={{
          id: x.id,
          name: x.name,
          lastMessage: x.message,
          totalUnreadCount: x.totalUnreadCount,
          lastReadMessageId: x.lastReadMessageId,
        }} />,
      }));
  }, [chats, searchValue]);
}

export function ChatGroupAside() {
  const { t } = useTranslation();
  const { chatId } = useChatGroupContext();
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useBool(false);
  const selected = useMemo(() => (chatId ? [chatId] : []), [chatId]);
  const onSelect = useOnSelect();
  const items = useMenuItems(searchValue);

  return (
    <div className="wxchtf-aside">
      <div className={styles.actions}>
        <div className={styles.searchBox}>
          <Input
            value={searchValue ?? ''}
            placeholder={isSearchFocused ? t('chats.searchPlaceholder') : t('search')}
            className={styles.search}
            addonBefore={<Icon type="search" />}
            onChange={(e) => setSearchValue(isEmpty(e.target.value) ? null : e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </div>
        {!isSearchFocused && <CreateChatButton />}
      </div>

      {items && (
        <Menu className="wxchtf-chat-list" selectedKeys={selected} onSelect={onSelect} items={items} />
      )}
    </div>
  );
}
